import JSZip from 'jszip';
import { useDebug } from '@/composables/useDebug';
import type { RawInsights, RawConsoleEntry, RawErrorEntry, RawHarLog, RawBrowserInfo } from './insightsRawTypes';

const { debug } = useDebug('viewer:logs');

/**
 * Filename suffixes that the Zight Insight SDK uses inside the insights.zip.
 * The actual filenames are `session-{sessionTsMs}-{sessionId}-{type}.json|har`,
 * so we match by suffix rather than by exact name.
 *
 * Keep this exported so a future SDK rename is a one-place change.
 */
export const INSIGHTS_FILE_SUFFIXES = {
  console: '-console.json',
  errors: '-errors.json',
  network: '-network.har',
  browserInfo: '-browserinfo.json',
} as const;

// ---------------------------------------------------------------------------
// Typed errors — let the store classify them into user-facing messages
// ---------------------------------------------------------------------------

export class InsightsNetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InsightsNetworkError';
  }
}

export class InsightsCorruptZipError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'InsightsCorruptZipError';
    if (cause instanceof Error) {
      this.cause = cause;
    }
  }
}

export class InsightsMissingFileError extends Error {
  constructor(suffix: string) {
    super(`Missing required file with suffix "${suffix}" in insights.zip`);
    this.name = 'InsightsMissingFileError';
  }
}

// ---------------------------------------------------------------------------
// downloadZip — thin fetch wrapper, kept separate from extract() so the
// extractor stays a pure function over an ArrayBuffer (easy to test).
// ---------------------------------------------------------------------------

export const downloadZip = async (url: string, signal?: AbortSignal): Promise<ArrayBuffer> => {
  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      throw e;
    }
    throw new InsightsNetworkError(`Failed to download insights archive: ${(e as Error)?.message ?? 'unknown'}`);
  }
  if (!response.ok) {
    throw new InsightsNetworkError(`Failed to download insights archive: HTTP ${response.status}`);
  }
  return response.arrayBuffer();
};

// ---------------------------------------------------------------------------
// extract — pure function over an ArrayBuffer
// ---------------------------------------------------------------------------

/**
 * Unzip an insights archive and parse the four expected files.
 *
 * Throws:
 * - `InsightsCorruptZipError` if JSZip fails to read the buffer
 * - `InsightsMissingFileError` if any of the required files is missing
 * - `SyntaxError` if any of the JSON files fails to parse (let the store
 *   classify this — it's distinct from a corrupt zip)
 */
export const extract = async (arrayBuffer: ArrayBuffer): Promise<RawInsights> => {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(arrayBuffer);
  } catch (e) {
    throw new InsightsCorruptZipError('Could not read insights archive', e);
  }

  const files = Object.values(zip.files).filter(f => !f.dir);
  const sessions = collectSessions(files.map(f => f.name));
  if (sessions.length > 1) {
    debug('[insightsExtractor] multi-session zip detected, using first session', sessions);
  }
  const sessionId = sessions[0]?.id;
  const sessionStartMs = sessions[0]?.ts;

  const consoleFile = findFileBySuffix(files, INSIGHTS_FILE_SUFFIXES.console, sessionId);
  const errorsFile = findFileBySuffix(files, INSIGHTS_FILE_SUFFIXES.errors, sessionId);
  const networkFile = findFileBySuffix(files, INSIGHTS_FILE_SUFFIXES.network, sessionId);
  const browserInfoFile = findFileBySuffix(files, INSIGHTS_FILE_SUFFIXES.browserInfo, sessionId);

  if (!consoleFile) throw new InsightsMissingFileError(INSIGHTS_FILE_SUFFIXES.console);
  if (!errorsFile) throw new InsightsMissingFileError(INSIGHTS_FILE_SUFFIXES.errors);
  if (!networkFile) throw new InsightsMissingFileError(INSIGHTS_FILE_SUFFIXES.network);
  if (!browserInfoFile) throw new InsightsMissingFileError(INSIGHTS_FILE_SUFFIXES.browserInfo);

  const [consoleText, errorsText, networkText, browserInfoText] = await Promise.all([
    consoleFile.async('string'),
    errorsFile.async('string'),
    networkFile.async('string'),
    browserInfoFile.async('string'),
  ]);

  return {
    console: JSON.parse(consoleText) as RawConsoleEntry[],
    errors: JSON.parse(errorsText) as RawErrorEntry[],
    network: JSON.parse(networkText) as RawHarLog,
    browserInfo: JSON.parse(browserInfoText) as RawBrowserInfo,
    sessionId,
    sessionStartMs,
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the first file whose name ends with the given suffix. When a session
 * id is provided, prefer files that also belong to that session — this keeps
 * us deterministic in the (unlikely) multi-session case.
 */
const findFileBySuffix = (files: JSZip.JSZipObject[], suffix: string, sessionId?: string): JSZip.JSZipObject | undefined => {
  if (sessionId) {
    const sessionMatch = files.find(f => f.name.includes(sessionId) && f.name.endsWith(suffix));
    if (sessionMatch) return sessionMatch;
  }
  return files.find(f => f.name.endsWith(suffix));
};

/**
 * Parse sessions out of the SDK's filename pattern:
 *   `session-{sessionStartMs}-{sessionId}-{type}.json|har`
 *
 * Returns the unique sessions found in the zip, sorted by embedded start
 * time ascending. The first entry is the earliest session — when a zip
 * contains multiple, we use that one as the canonical session.
 *
 * The `ts` (session start time) is also what `extract()` exposes as
 * `RawInsights.sessionStartMs` — it's the anchor the transformer uses to
 * compute "0:00 = recording start".
 */
interface SessionInfo {
  id: string;
  ts: number;
}

const collectSessions = (filenames: string[]): SessionInfo[] => {
  const pattern = /^session-(\d+)-([^-]+)-/;
  const seen = new Map<string, number>();
  for (const name of filenames) {
    const match = name.match(pattern);
    if (!match) continue;
    const ts = parseInt(match[1], 10);
    const id = match[2];
    if (!seen.has(id)) seen.set(id, ts);
  }
  return Array.from(seen.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([id, ts]) => ({ id, ts }));
};
